import React from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { formatNumber } from '../utils/formatters';
import { DateService } from '../services/dateService';

const { FiAward, FiCalendar, FiGift, FiTrendingUp, FiTarget, FiCheck } = FiIcons;

const HERO_IMAGE = "https://cdn.midjourney.com/9697fcc3-16b9-4193-a351-57ccb8eb4fc3/0_1.webp";

export default function StreakDaily() {
  const { state, actions } = useGame();
  const today = DateService.getDayKey();
  
  const canClaim = state.daily.completedToday && state.daily.lastClaimDay !== today;
  const currentReward = state.daily.rewards[state.daily.index];
  const hasCompletedFocusToday = state.daily.completedToday;

  const getClaimHint = () => {
    if (!hasCompletedFocusToday) {
      return "Complete a focus session today to unlock your reward.";
    }
    if (state.daily.lastClaimDay === today) {
      return "Reward claimed. Come back tomorrow!";
    }
    return "Your reward is ready to harvest!";
  };

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative h-48 lg:h-64 rounded-3xl overflow-hidden shadow-xl">
        <div className="absolute inset-0 bg-black/30 z-10" />
        <img 
          src={HERO_IMAGE} 
          alt="Golden Streak Reward" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 lg:p-8">
          <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-white mb-2 text-shadow-sm">
            Daily Rewards
          </h1>
          <p className="text-white/90 font-dm-sans max-w-lg">
            Consistency is the key to a flourishing garden. Maintain your streak to earn compounding rewards.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Claim Area */}
        <Card className="p-8 lg:col-span-2 flex flex-col justify-center text-center relative overflow-hidden bg-gradient-to-br from-white to-goldenrod/5">
          <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
            <SafeIcon icon={FiGift} className="w-64 h-64 text-goldenrod" />
          </div>
          
          <div className="relative z-10">
            <div className="w-20 h-20 bg-gradient-to-br from-goldenrod to-sage rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-goldenrod/20">
              <SafeIcon icon={FiGift} className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-2xl font-cormorant font-bold text-charcoal mb-2">Today's Harvest</h2>
            <p className="text-charcoal/60 font-dm-sans mb-8 max-w-md mx-auto">{getClaimHint()}</p>

            <div className="mb-8">
              <div className="text-sm text-charcoal/50 uppercase tracking-widest font-bold mb-2">Reward Value</div>
              <div className="text-5xl font-bold text-charcoal font-cormorant">
                {formatNumber(currentReward)} <span className="text-2xl text-sage">Seeds</span>
              </div>
            </div>

            <Button 
              variant={canClaim ? "warning" : "secondary"} 
              disabled={!canClaim} 
              onClick={actions.claimDaily} 
              size="lg" 
              icon={FiGift}
              className={`px-12 py-4 text-lg shadow-lg transition-all ${canClaim ? 'hover:shadow-goldenrod/30 hover:scale-105' : ''}`}
            >
              {canClaim ? 'Claim Reward' : 'Locked'}
            </Button>
          </div>
        </Card>

        {/* Streak Stats */}
        <div className="space-y-6">
          <Card className="p-6 bg-gradient-to-br from-sage/10 to-transparent">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm text-sage">
                <SafeIcon icon={FiAward} className="w-6 h-6" />
              </div>
              <div>
                <div className="text-3xl font-bold text-charcoal">{state.streak.current}</div>
                <div className="text-xs text-charcoal/60 uppercase tracking-wider font-bold">Current Streak</div>
              </div>
            </div>
            <div className="h-2 bg-white/50 rounded-full overflow-hidden">
              <div className="h-full bg-sage w-full animate-pulse-soft" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-skyblue/10 to-transparent">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm text-skyblue">
                <SafeIcon icon={FiTrendingUp} className="w-6 h-6" />
              </div>
              <div>
                <div className="text-3xl font-bold text-charcoal">{state.streak.best}</div>
                <div className="text-xs text-charcoal/60 uppercase tracking-wider font-bold">Best Streak</div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-dm-sans font-bold text-charcoal mb-3">Streak Rules</h3>
            <ul className="space-y-3 text-sm text-charcoal/70">
              <li className="flex items-start gap-2">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-sage mt-0.5" />
                <span>Complete 1 focus session daily</span>
              </li>
              <li className="flex items-start gap-2">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-sage mt-0.5" />
                <span>Claim within 24 hours</span>
              </li>
              <li className="flex items-start gap-2">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-sage mt-0.5" />
                <span>Miss a day, reset to Day 1</span>
              </li>
            </ul>
          </Card>
        </div>
      </div>

      {/* 7-Day Progress */}
      <Card className="p-6 lg:p-8">
        <h3 className="text-xl font-cormorant font-bold text-charcoal mb-6">7-Day Forecast</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          {state.daily.rewards.map((reward, index) => {
            const isCompleted = index < state.daily.index;
            const isCurrent = index === state.daily.index;
            
            return (
              <div 
                key={index}
                className={`
                  relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center text-center gap-2 transition-all
                  ${isCurrent 
                    ? 'border-goldenrod bg-goldenrod/5 shadow-lg shadow-goldenrod/10 scale-105 z-10' 
                    : isCompleted 
                      ? 'border-sage bg-sage/5 opacity-60' 
                      : 'border-transparent bg-gray-50'}
                `}
              >
                <div className="text-xs text-charcoal/40 font-bold uppercase tracking-wider">Day {index + 1}</div>
                <div className={`font-bold text-lg ${isCurrent ? 'text-charcoal' : 'text-charcoal/60'}`}>
                  {formatNumber(reward)}
                </div>
                {isCompleted && (
                  <div className="absolute top-2 right-2 text-sage">
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 bg-goldenrod text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    TODAY
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}